import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaChartBar, FaFileAlt, FaSpinner, FaCog, FaTags } from 'react-icons/fa';
import HeaderCard from '../../components/Header/HeaderCard';
import SecondarySidebar from '../../components/SideBar/SecondarySidebar';
import Recapitulatif from './Etats/Recapitulatif';
import CustomReportBuilder from './Etats/CustomReportBuilder';
import CustomReportManager from './Etats/CustomReportManager';
import { apiService } from '../../components/Config/api.service';
import Select from 'react-select';
import { TAG_COLORS } from '../../components/Constantes/Colors';

const FinancialStatements = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totals, setTotals] = useState([]);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [monthlyGrid, setMonthlyGrid] = useState(null);
  const [id, setId] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [showDesc, setShowDesc] = useState(true);
  const [lastGenerated, setLastGenerated] = useState(null);

  // Nouveaux états pour le filtrage par catégories
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [addChildCategories, setAddChildCategories] = useState(false);

  // Filtrage par département : appliqué à la sélection des bulletins côté
  // backend, il vaut donc pour tous les états.
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  // Local date formatter to YYYY-MM-DD (no timezone shift)
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const setRange = (fromDate, toDate) => {
    setStartDate(fmt(fromDate));
    setEndDate(fmt(toDate));
  };

  // Dev-only quick tests to ensure sanitizer strips scripts and event handlers
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const malicious = '<img src=x onerror=alert(1) /><script>window._xss=1</script><a href="javascript:alert(1)">bad</a><b>ok</b>';
    const out = sanitizeHtml(malicious);
    // Basic console checks visible to developer
    console.log('[sanitizer] input:', malicious);
    console.log('[sanitizer] output:', out);
    if (out.includes('onerror') || out.includes('javascript:') || out.includes('script')) {
      console.error('[sanitizer] Failed to remove dangerous content');
    } else {
      console.log('[sanitizer] Sanitizer looks OK');
    }
  }, []);
  const presets = {
    thisMonth: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0));
    },
    lastMonth: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0));
    },
    thisYear: () => {
      const now = new Date();
      setRange(new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31));
    },
  };

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
    fetchDepartments();
  }, []);

  // Nouvelle fonction pour récupérer les catégories
  const fetchCategories = async () => {
    try {
      const response = await apiService.getElement('/employee/categories');
      const cats = response.data.categories || [];
      const options = cats.map(cat => ({
        value: cat.id,
        label: cat.name,
        color: cat.color || 1,
      }));
      setCategoryOptions(options);
    } catch (err) {
      console.error('Erreur lors de la récupération des catégories:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await apiService.getElement('/employee/departments');
      const departments = response.data.departments || [];
      setDepartmentOptions(
        departments
          .filter(dep => dep.active !== false)
          .map(dep => ({ value: dep.id, label: dep.name }))
      );
    } catch (err) {
      console.error('Erreur lors de la récupération des départements:', err);
    }
  };

  // fetch function réutilisable (rafraîchissement après save/archive/delete)
  const fetchMenuItems = async () => {
    try {
      const lang = i18n.language || 'fr';
      const response = await apiService.getElement(`/reports?lang=${lang}`);
      const data = response.data;
      if (data.status === 'success') {
        const { getCountryConfig } = await import('../../components/Config/countries');
        const config = getCountryConfig();
        const allowedReports = config.allowedReports;

        const filteredItems = data.data
          .map((report) => {
            const isCustomReport = (report.report_name && String(report.report_name).startsWith('custom_report_')) || String(report.id).startsWith('custom_');
            const rawName = report.raw_name || report.name || '';
            const rawDesc = report.description || '';
            
            // Map backend report names to i18n keys for dynamic translation
            const reportNameMap = {
              'Bilan social': t('financialStatements.socialReport', 'Bilan social'),
              'Livre de paie': t('financialStatements.payrollLedger', 'Livre de paie'),
              'Charges patronales': t('financialStatements.employerCharges', 'Charges patronales'),
              'Charges salariales': t('financialStatements.employeeCharges', 'Charges salariales'),
            };

            const translatedLabel = reportNameMap[rawName] || t(`financialStatements.reports.${rawName}`, rawName);
            const reportDescMap = {
              'Bilan social': t('financialStatements.socialReportDescription', rawDesc),
              'Livre de paie': t('financialStatements.payrollLedgerDescription', rawDesc),
              'Charges patronales': t('financialStatements.employerChargesDescription', rawDesc),
              'Charges salariales': t('financialStatements.employeeChargesDescription', rawDesc),
            };

            const translatedDesc = reportDescMap[rawName] || (rawDesc && rawDesc.includes('Bilan social') ? t('financialStatements.socialReportDescription', rawDesc) : t(`financialStatements.descriptions.${rawName}`, rawDesc));

            return {
              id: report.id,
              rawName: rawName,
              label: translatedLabel,
              report_name: report.report_name,
              hasExcel: isCustomReport || report.has_excel,
              hasWord: report.has_word || false,
              description: translatedDesc,
              isCustom: isCustomReport,
            };
          })
          .filter(report => {
            if (report.isCustom) return true;
            
            // Si aucune restriction n'est configurée, tout afficher
            if (!allowedReports || !Array.isArray(allowedReports)) return true;

            // En anglais, ajouter les équivalents anglais pour la correspondance des filtres
            const nameToMatch = (report.rawName || report.label || '').toLowerCase();
            const repNameToMatch = (report.report_name || '').toLowerCase();

            return allowedReports.some(allowed => {
              const a = allowed.toLowerCase();
              if (nameToMatch.includes(a) || repNameToMatch.includes(a)) return true;
              
              // Correspondance croisée FR <-> EN pour allowedReports
              if (a.includes('bilan social') && (nameToMatch.includes('social report') || repNameToMatch.includes('bilan_social'))) return true;
              if (a.includes('livre de paie') && (nameToMatch.includes('payroll ledger') || repNameToMatch.includes('book'))) return true;
              if (a.includes('charges patronales') && (nameToMatch.includes('employer charges') || repNameToMatch.includes('employer_charges'))) return true;
              if (a.includes('charges salariales') && (nameToMatch.includes('wage costs') || nameToMatch.includes('employee charges') || repNameToMatch.includes('wage_costs'))) return true;

              return false;
            });
          })
          .sort((a, b) => {
            // 1. Rapports par défaut avant les personnalisés
            if (!a.isCustom && b.isCustom) return -1;
            if (a.isCustom && !b.isCustom) return 1;
            
            // 2. Parmi les rapports par défaut, ceux avec "etat" en dernier
            if (!a.isCustom && !b.isCustom) {
              const aHasEtat = a.label.toLowerCase().includes('etat');
              const bHasEtat = b.label.toLowerCase().includes('etat');
              if (aHasEtat && !bHasEtat) return 1;
              if (!aHasEtat && bHasEtat) return -1;
            }
            
            // 3. Ordre alphabétique pour le reste
            return a.label.localeCompare(b.label);
          });
        
        setMenuItems(filteredItems);
        setActiveTab(filteredItems[0]?.id || null);
      } else {
        throw new Error(data.message || 'Failed to fetch reports');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    if (activeTab && startDate && endDate) {
      handleGenerateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Re-fetch reports dynamically whenever language changes
  useEffect(() => {
    fetchMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Sanitizer: prefer DOMPurify when available, otherwise fallback to a safe DOMParser-based sanitizer.
  const sanitizeHtml = (dirty) => {
    if (!dirty) return '';
    try {
      const DOMPurify = typeof window !== 'undefined' ? window.DOMPurify : undefined;
      if (DOMPurify && typeof DOMPurify.sanitize === 'function') {
        return DOMPurify.sanitize(dirty, { USE_PROFILES: { html: true } });
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(dirty, 'text/html');
      const allowedTags = new Set(['b','i','em','strong','p','br','ul','ol','li','a','span','div','h1','h2','h3','h4','h5','h6']);
      const ELEMENT_NODE = (typeof Node !== 'undefined') ? Node.ELEMENT_NODE : 1;

      const walk = (node) => {
        if (!node || !node.parentNode) return;
        if (node.nodeType === ELEMENT_NODE) {
          const nodeName = node.nodeName.toLowerCase();
          if (nodeName === 'body' || nodeName === 'html') {
            let child = node.firstChild;
            while (child) {
              const next = child.nextSibling;
              walk(child);
              child = next;
            }
            return;
          }
          if (!allowedTags.has(nodeName)) {
            const text = doc.createTextNode(node.textContent);
            node.parentNode.replaceChild(text, node);
            return; // replaced, no need to recurse this node
          }

          // remove dangerous attributes
          for (let i = node.attributes.length - 1; i >= 0; i--) {
            const attr = node.attributes[i];
            const name = attr.name;
            const value = attr.value || '';
            if (name.startsWith('on')) {
              node.removeAttribute(name);
              continue;
            }
            if (name === 'href') {
              const val = value.trim().toLowerCase();
              if (!val.startsWith('http://') && !val.startsWith('https://') && !val.startsWith('/')) {
                node.removeAttribute('href');
              }
              continue;
            }
            if (!['class', 'id', 'title', 'style'].includes(name)) {
              node.removeAttribute(name);
            }
          }
        }

        let child = node.firstChild;
        while (child) {
          const next = child.nextSibling;
          walk(child);
          child = next;
        }
      };

      walk(doc.body);
      return doc.body.innerHTML;
    } catch (e) {
      return '';
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert(t('financialStatements.selectPeriodAlert'));
      return;
    }

    const selectedMenuName =
      menuItems.find((item) => item.id === activeTab)?.report_name || 'Récapitulatif';
    setReportLoading(true);

    try {
      const requestData = {
        date_from: startDate,
        date_to: endDate,
        menu_name: selectedMenuName,
        category_ids: selectedCategories.map(cat => cat.value),
        department_ids: selectedDepartments.map(dep => dep.value),
        add_child_categories: addChildCategories,
      };

      const response = await apiService.saveElement('/generate-report', requestData);
  
      if (response.data.result.status === 'success') {
        setId(response.data.result.report_id); 
        setTotals(response.data.result.totals); 
        setPaymentDetails(response.data.result.payment_details || {});
        setMonthlyGrid(response.data.result.monthly_grid || null);
        console.log('Report generated successfully:', response.data.result.payment_details);
        setLastGenerated({ 
          startDate, 
          endDate, 
          activeTab,
          selectedCategories: [...selectedCategories],
          selectedDepartments: [...selectedDepartments],
          addChildCategories 
        });
      } else {
        throw new Error(response.data.message || t('financialStatements.generateReportError'));
      }
    } catch (err) {
      console.error('Erreur lors de la génération du rapport:', err);
      setError(t('financialStatements.generateReportError'));
    } finally {
      setReportLoading(false);
    }
  };

  const renderContent = () => {
    if (!activeTab) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-gray-100 rounded-full p-4 mb-4">
            <FaFileAlt className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('financialStatements.noReportSelected')}</h3>
          <p className="text-gray-500">{t('financialStatements.selectReportHint')}</p>
        </div>
      );
    }

    const selectedMenu = menuItems.find((item) => item.id === activeTab);
    const selectedMenuName = selectedMenu?.label || 'Récapitulatif';
    const reportName = selectedMenu?.report_name || '';
    const hasExcel = selectedMenu?.hasExcel || false;
    const hasWord = selectedMenu?.hasWord || false;
    const isCustom = reportName && String(reportName).startsWith('custom_report_');
    const description = selectedMenu?.description || t('financialStatements.descriptionNotAvailable');
    const safeDescription = sanitizeHtml(description);

    return (
      <div className="space-y-4">
        {/* Récapitulatif Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200" aria-live="polite">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-[--bg-color] rounded-full p-2">
                <FaChartBar className="text-[--color-primary]" size={16} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{t('financialStatements.summary')}</h3>
            </div>
          </div>
          <div className="p-6">
            {reportLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-6 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-24 bg-gray-200 rounded" />
              </div>
            ) : (
              <Recapitulatif
                selectedMenuName={selectedMenuName}
                reportName={reportName}
                isCustom={isCustom}
                totals={totals}
                monthlyGrid={monthlyGrid}
                id={id}
                hasExcel={hasExcel}
                hasWord={hasWord}
                paymentDetails={paymentDetails}
                setPaymentDetails={setPaymentDetails}
                dateFrom={startDate}
                dateTo={endDate}
              />
            )}
          </div>
        </div>

        {/* Description Card (collapsible) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[--bg-color] rounded-full p-2">
                <FaFileAlt className="text-[--color-primary]" size={16} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{t('financialStatements.reportDescription')}</h3>
            </div>
            <button
              onClick={() => setShowDesc((v) => !v)}
              className="text-sm text-[--color-primary] hover:underline"
              aria-expanded={showDesc}
            >
              {showDesc ? t('financialStatements.hide') : t('financialStatements.show')}
            </button>
          </div>
          {showDesc && (
            <div className="p-6">
              <div className="prose max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: safeDescription }} />
            </div>
          )}
        </div>
      </div>
    );
  };

  // if (loading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen">
  //       <div className="text-center">
  //         <div className="bg-[--bg-color] rounded-full p-4 mb-4 inline-block">
  //           <FaSpinner className="text-[--color-primary] animate-spin text-2xl" />
  //         </div>
  //         <p className="text-gray-600">Chargement des rapports...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="bg-red-100 rounded-full p-4 mb-4 inline-block">
            <FaFileAlt className="text-red-600 text-2xl" />
          </div>
          <p className="text-red-600 font-medium">{t('financialStatements.errorPrefix')}: {error}</p>
        </div>
      </div>
    );
  }

  // Style personnalisé pour react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      '&:hover': {
        borderColor: 'var(--color-primary)',
      },
    }),
    multiValue: (provided, state) => {
      const color = TAG_COLORS[(state.data.color || 1) - 1];
      return {
        ...provided,
        backgroundColor: color + '20',
        borderRadius: '9999px',
        padding: '2px 8px',
      };
    },
    multiValueLabel: (provided, state) => {
      const color = TAG_COLORS[(state.data.color || 1) - 1];
      return {
        ...provided,
        color: color,
        fontWeight: '500',
      };
    },
    multiValueRemove: (provided, state) => {
      const color = TAG_COLORS[(state.data.color || 1) - 1];
      return {
        ...provided,
        color: color,
        ':hover': {
          backgroundColor: color,
          color: 'white',
        },
      };
    },
  };

  return (
    <div className="">
      {/* Header */}
      <HeaderCard
        title={t('financialStatements.title')}
        text={t('financialStatements.subtitle')}
      />

      {/* Date Selection Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-[--bg-color] rounded-full p-2">
              <FaCalendarAlt className="text-[--color-primary]" size={16} />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">{t('financialStatements.periodSelection')}</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.thisMonth}>
              {t('financialStatements.thisMonth')}
            </button>
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.lastMonth}>
              {t('financialStatements.lastMonth')}
            </button>
            <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={presets.thisYear}>
              {t('financialStatements.thisYear')}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            <div className="flex-1 space-y-4 sm:space-y-0 sm:flex sm:gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('financialStatements.startDate')} *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStartDate(v);
                    // Ensure end date stays >= start date
                    if (endDate && v && endDate < v) setEndDate('');
                  }}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                  max={endDate || undefined}
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('financialStatements.endDate')} *
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[--color-primary]"
                />
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                className="flex items-center gap-2 px-6 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--color-primary] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                onClick={handleGenerateReport}
                disabled={
                  !startDate ||
                  !endDate ||
                  reportLoading ||
                  (lastGenerated &&
                    lastGenerated.startDate === startDate &&
                    lastGenerated.endDate === endDate &&
                    lastGenerated.activeTab === activeTab &&
                    JSON.stringify(lastGenerated.selectedCategories) === JSON.stringify(selectedCategories) &&
                    JSON.stringify(lastGenerated.selectedDepartments) === JSON.stringify(selectedDepartments) &&
                    lastGenerated.addChildCategories === addChildCategories)
                }
              >
                {reportLoading ? (
                  <>
                    <FaSpinner className="animate-spin" size={16} />
                    {t('financialStatements.generating')}
                  </>
                ) : (
                  <>
                    <FaChartBar size={16} />
                    {t('financialStatements.generateStatements')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Filtrage par étiquettes */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-[--bg-color] rounded-full p-2">
                <FaTags className="text-[--color-primary]" size={14} />
              </div>
              <h4 className="text-sm font-semibold text-gray-800">{t('financialStatements.filterByTags')}</h4>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('financialStatements.selectTags')}
                </label>
                <Select
                  isMulti
                  isClearable
                  options={categoryOptions}
                  value={selectedCategories}
                  onChange={setSelectedCategories}
                  placeholder={t('financialStatements.noFilterPlaceholder')}
                  styles={customSelectStyles}
                  noOptionsMessage={() => t('financialStatements.noTagsAvailable')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('financialStatements.selectDepartments', 'Départements')}
                </label>
                <Select
                  isMulti
                  isClearable
                  options={departmentOptions}
                  value={selectedDepartments}
                  onChange={setSelectedDepartments}
                  placeholder={t('financialStatements.noFilterPlaceholder')}
                  styles={customSelectStyles}
                  noOptionsMessage={() => t('financialStatements.noDepartmentsAvailable', 'Aucun département')}
                />
              </div>

              {selectedCategories.length > 0 && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="addChildCategories"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={addChildCategories}
                    onChange={(e) => setAddChildCategories(e.target.checked)}
                  />
                  <label htmlFor="addChildCategories" className="text-sm text-gray-700 cursor-pointer">
                    {t('financialStatements.includeSubtags')}
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Selected period summary + clear */}
          {startDate && endDate ? (
            <div className="text-sm text-gray-700 flex flex-wrap items-center gap-3">
              <span className="px-2 py-1 bg-gray-100 rounded">
                {t('financialStatements.periodLabel')}: {startDate} → {endDate}
              </span>
              {selectedCategories.length > 0 && (
                <span className="px-2 py-1 bg-blue-100 rounded flex items-center gap-1">
                  <FaTags size={12} />
                  {t('financialStatements.tagsCount', { count: selectedCategories.length })}
                </span>
              )}
              {selectedDepartments.length > 0 && (
                <span className="px-2 py-1 bg-blue-100 rounded flex items-center gap-1">
                  {t('financialStatements.departmentsCount', {
                    count: selectedDepartments.length,
                    defaultValue: '{{count}} département(s)',
                  })}
                </span>
              )}
              <button
                className="text-xs text-red-600 hover:underline"
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                  setSelectedCategories([]);
                  setSelectedDepartments([]);
                  setAddChildCategories(false);
                }}
              >
                {t('financialStatements.resetAll')}
              </button>
            </div>
          ) : null}

          {!startDate || !endDate ? (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                ⚠️ {t('financialStatements.selectDatesHint')}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4">
        {/* Sidebar (sticky) */}
        <div className="w-80 flex-shrink-0 sticky top-4 self-start">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">{t('financialStatements.reportTypes')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('financialStatements.chooseReport')}</p>
            </div>
            <div className="p-2">
              {/* Gestion des rapports personnalisés */}
              <div className="mb-2">
                <button
                  onClick={() => setShowManager(true)}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[--color-primary] text-[--color-primary] rounded-lg hover:bg-[--bg-color] transition-colors"
                >
                  <FaCog size={14} />
                  {t('financialStatements.manageCustomReports')}
                </button>
              </div>
              
              {/* Scrollable container for sidebar */}
              <div className="max-h-[calc(150vh-24rem)] overflow-y-auto">
                <SecondarySidebar
                  menuItems={menuItems}
                  setActiveTab={setActiveTab}
                  activeTab={activeTab}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">{renderContent()}</div>
      </div>

      {/* Modals */}
      {showManager && (
        <CustomReportManager
          onClose={() => setShowManager(false)}
          onCreate={() => {
            setShowManager(false);
            setEditingReport(null);
            setShowBuilder(true);
          }}
          onEdit={(report) => {
            setShowManager(false);
            setEditingReport(report);
            setShowBuilder(true);
          }}
          menuItems={menuItems}
        />
      )}

      {showBuilder && (
        <CustomReportBuilder
          onClose={() => {
            setShowBuilder(false);
            setEditingReport(null);
          }}
          onSaved={async () => {
            await fetchMenuItems();
            setShowBuilder(false);
            setEditingReport(null);
          }}
          editingReport={editingReport}
        />
      )}
    </div>
  );
};

export default FinancialStatements;